import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../utils/api";
import AsyncSelect from "react-select/async";

const AddManualPayment = ({ show, onClose, onSuccess }) => {
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    user_id: "",
    plan_id: "",
    coupon_code: "",
    payment_method: "manual",
    transaction_id: "",
    remarks: ""
  });

  const [amount, setAmount] = useState({
    planAmount: 0,
    discount: 0,
    total: 0
  });

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (show) {
      fetchPlans();
      fetchCoupons();
    } else {
      resetForm();
    }
  }, [show]);

  const fetchPlans = async () => {
    try {
      const res = await api.get("plans/active");
      setPlans(res.data.data || []);
    } catch {
      toast.error("Failed to load plans");
    }
  };

  const fetchCoupons = async () => {
    try {
      const res = await api.get("coupons/active");
      setCoupons(res.data.data || []);
    } catch {
      toast.error("Failed to load coupons");
    }
  };

  /* ================= SEARCH USERS ================= */
  const loadOptions = useCallback(async (inputValue) => {
    try {
      const res = await api.get("get-all-users", {
        params: {
          search: inputValue || "",
          limit: 10,
          page: 1
        }
      });

      const users = res.data.data || [];
      
      return users.map(user => ({
        value: user._id,
        // label: `${user.owner} (${user.email})${user.shop_type ? ` - ${user.shop_type}` : ''}`,
        label: `${user.shop_type} - ${user.owner}(${user.email ? ` ${user.email}` : ''})`,
        user: user // Store full user object for reference
      }));
    } catch (error) {
      console.error("Failed to search users:", error);
      toast.error("Failed to search users");
      return [];
    }
  }, []);

  /* ================= RESET ================= */
  const resetForm = () => {
    setForm({
      user_id: "",
      plan_id: "",
      coupon_code: "",
      payment_method: "manual",
      transaction_id: "",
      remarks: ""
    });
    setAmount({ planAmount: 0, discount: 0, total: 0 });
  };

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUserSelect = (selectedOption) => {
    setForm(prev => ({
      ...prev,
      user_id: selectedOption ? selectedOption.value : ""
    }));
  };

  /* ================= AMOUNT CALC ================= */
  useEffect(() => {
    const plan = plans.find(p => p._id === form.plan_id);
    if (!plan) {
      setAmount({ planAmount: 0, discount: 0, total: 0 });
      return;
    }

    let discount = 0;
    const coupon = coupons.find(c => c.code === form.coupon_code);

    if (coupon) {
      discount =
        coupon.discount_type === "percentage"
          ? Math.min(
              (plan.amount * coupon.discount_value) / 100,
              coupon.max_discount || Infinity
            )
          : coupon.discount_value;
    }

    discount = Math.min(discount, plan.amount);

    setAmount({
      planAmount: plan.amount,
      discount,
      total: plan.amount - discount
    });
  }, [form.plan_id, form.coupon_code, plans, coupons]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!form.user_id || !form.plan_id) {
      return toast.error("User and Plan are required");
    }

    // Validate total amount
    if (amount.total <= 0) {
      return toast.error("Total amount must be greater than 0");
    }

    try {
      setLoading(true);
      await api.post("payments/admin-create", {
        ...form,
        total_amount: amount.total,
        discount_amount: amount.discount
      });
      toast.success("Payment added & plan activated");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Payment creation error:", err);
      toast.error(err.response?.data?.message || "Failed to add payment");
    } finally {
      setLoading(false);
    }
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "44px",
      borderColor: "#d1d5db",
      "&:hover": {
        borderColor: "#9ca3af",
      },
      borderRadius: "0.375rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#f3f4f6" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#374151",
      padding: "10px 12px",
      fontSize: "14px",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.375rem",
      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
      zIndex: 9999,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "14px",
    }),
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl relative max-h-[90vh] overflow-y-auto">
        
        {/* HEADER */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Add Manual Payment</h3>
              <p className="text-sm text-gray-500 mt-1">Add payment and activate user plan</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* FORM */}
        <div className="p-6 space-y-4">
          {/* STORE SELECTION WITH ASYNC SEARCH */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Store *
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadOptions}
              onChange={handleUserSelect}
              placeholder="Search store by name, email, or owner..."
              noOptionsMessage={({ inputValue }) => 
                inputValue ? "No stores found" : "Type to search stores..."
              }
              loadingMessage={() => "Searching..."}
              styles={customStyles}
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <p className="text-xs text-gray-500 mt-2">
              Start typing to search for stores by name, email, or owner
            </p>
          </div>

          {/* PLAN SELECTION */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Plan *
            </label>
            <select
              name="plan_id"
              value={form.plan_id}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
            >
              <option value="">Choose a subscription plan</option>
              {plans.map(p => (
                <option key={p._id} value={p._id}>
                  {p.title} – ₹{p.amount} / {p.duration_days} days
                  {p.highlighting_feature && ` (${p.highlighting_feature})`}
                </option>
              ))}
            </select>
          </div>

          {/* COUPON SELECTION */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apply Coupon (Optional)
            </label>
            <select
              name="coupon_code"
              value={form.coupon_code}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="">Select a coupon (optional)</option>
              {coupons.map(c => (
                <option key={c._id} value={c.code}>
                  {c.code} - {c.discount_type === "percentage" ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                  {c.valid_until && ` (Valid till: ${new Date(c.valid_until).toLocaleDateString()})`}
                </option>
              ))}
            </select>
          </div>

          {/* PAYMENT METHOD */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <select
              name="payment_method"
              value={form.payment_method}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
            >
              <option value="manual">Manual Payment</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
            </select>
          </div>

          {/* TRANSACTION ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction / Reference ID
              <span className="text-gray-500 text-sm ml-1">(Optional)</span>
            </label>
            <input
              type="text"
              name="transaction_id"
              value={form.transaction_id}
              onChange={handleChange}
              placeholder="e.g., TXN123456, UPI ref, Cheque no."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          {/* REMARKS */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remarks / Notes
              <span className="text-gray-500 text-sm ml-1">(Optional)</span>
            </label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              placeholder="Add any notes about this payment..."
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
            />
          </div>

          {/* AMOUNT SUMMARY */}
          {form.plan_id && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 mt-4">
              <h4 className="font-semibold text-gray-800 mb-4">Payment Summary</h4>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Plan Amount</span>
                  <span className="font-medium">₹{amount.planAmount.toFixed(2)}</span>
                </div>
                
                {amount.discount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Discount Applied</span>
                    <span className="font-medium">- ₹{amount.discount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-gray-300 pt-3 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-800">Total Payable</span>
                    <span className="text-2xl font-bold text-blue-600">₹{amount.total.toFixed(2)}</span>
                  </div>
                  {form.coupon_code && amount.discount > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Coupon "{form.coupon_code}" applied successfully
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading || !form.user_id || !form.plan_id}
              className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Add Payment & Activate Plan"
              )}
            </button>
          </div>
          
          {(!form.user_id || !form.plan_id) && (
            <p className="text-sm text-amber-600 mt-3 text-center">
              * Please select a store and plan to proceed
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddManualPayment;